import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { BookOpen, Plus, Trash2, Calendar, ClipboardList, CheckCircle2, Pencil } from "lucide-react";
import { AttachmentPicker, AttachmentList } from "./Attachments";

/** Teacher-side homework panel for a single class. */
export function TeacherHomework({ classId }) {
    const [items, setItems] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [openSubs, setOpenSubs] = React.useState(null); // hw record

    const load = React.useCallback(() => {
        if (!classId) return;
        api.get("/homework", { params: { class_id: classId } }).then(r => setItems(r.data));
    }, [classId]);
    React.useEffect(() => { load(); }, [load]);

    async function remove(hw) {
        if (!window.confirm(`Delete "${hw.title}" and all submissions?`)) return;
        await api.delete(`/homework/${hw.id}`);
        toast.success("Homework deleted");
        load();
    }

    return (
        <Card>
            <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-primary" />
                        <h2 className="font-dash text-xl font-semibold">Homework</h2>
                    </div>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-homework-btn" className="bg-primary hover:bg-primary/90 gap-2">
                                <Plus className="w-4 h-4" /> Assign homework
                            </Button>
                        </DialogTrigger>
                        <NewHomeworkDialog classId={classId} onSaved={() => { setOpen(false); load(); }} />
                    </Dialog>
                </div>

                {items.length === 0 ? (
                    <div className="text-brand-stone italic text-sm py-6 text-center">No homework assigned yet.</div>
                ) : (
                    <div className="space-y-3">
                        {items.map(hw => (
                            <div key={hw.id} className="border border-border rounded-lg p-4" data-testid={`hw-${hw.id}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-dash font-semibold">{hw.title}</div>
                                        {hw.description && <p className="text-sm text-brand-stone mt-1 whitespace-pre-wrap">{hw.description}</p>}
                                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                                            {hw.due_date && (
                                                <span className="inline-flex items-center gap-1 text-brand-stone">
                                                    <Calendar className="w-3 h-3" /> Due {hw.due_date}
                                                </span>
                                            )}
                                            <Badge className="bg-primary/10 text-primary border-0">
                                                {hw.submission_count} submission{hw.submission_count === 1 ? "" : "s"}
                                            </Badge>
                                        </div>
                                        <AttachmentList items={hw.attachments} testid={`hw-${hw.id}-att`} />
                                    </div>
                                    <div className="flex flex-col gap-1.5 shrink-0">
                                        <Button size="sm" variant="outline" onClick={() => setOpenSubs(hw)} data-testid={`view-subs-${hw.id}`} className="gap-1.5 h-8">
                                            <ClipboardList className="w-3.5 h-3.5" /> Submissions
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => remove(hw)} data-testid={`delete-hw-${hw.id}`} className="gap-1.5 h-8 text-brand-stone hover:text-destructive">
                                            <Trash2 className="w-3.5 h-3.5" /> Delete
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
            <SubmissionsDialog hw={openSubs} onClose={() => setOpenSubs(null)} onGraded={load} />
        </Card>
    );
}

function NewHomeworkDialog({ classId, onSaved }) {
    const [form, setForm] = React.useState({ title: "", description: "", due_date: "", attachments: [] });
    const [busy, setBusy] = React.useState(false);
    async function submit() {
        if (!form.title.trim()) return toast.error("Title is required");
        setBusy(true);
        try {
            await api.post("/homework", { class_id: classId, ...form, due_date: form.due_date || null });
            toast.success("Homework assigned");
            setForm({ title: "", description: "", due_date: "", attachments: [] });
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not assign");
        } finally { setBusy(false); }
    }
    return (
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Assign homework</DialogTitle></DialogHeader>
            <div className="space-y-3">
                <div>
                    <Label>Title *</Label>
                    <Input data-testid="hw-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Memorize Surah Al-Fatiha" className="mt-1.5" />
                </div>
                <div>
                    <Label>Instructions</Label>
                    <Textarea data-testid="hw-description-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={4} className="mt-1.5" />
                </div>
                <div>
                    <Label>Due date</Label>
                    <Input type="date" data-testid="hw-due-date-input" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                    <Label>Attachments</Label>
                    <div className="mt-1.5">
                        <AttachmentPicker value={form.attachments} onChange={v => setForm({ ...form, attachments: v })} testid="hw-attachments" />
                    </div>
                </div>
                <Button data-testid="save-hw-btn" onClick={submit} disabled={busy} className="w-full bg-primary hover:bg-primary/90">
                    {busy ? "Saving…" : "Assign homework"}
                </Button>
            </div>
        </DialogContent>
    );
}

function SubmissionsDialog({ hw, onClose, onGraded }) {
    const [data, setData] = React.useState(null);
    const load = React.useCallback(() => {
        if (!hw) return;
        api.get(`/homework/${hw.id}/submissions`).then(r => setData(r.data));
    }, [hw]);
    React.useEffect(() => { load(); }, [load]);
    return (
        <Dialog open={!!hw} onOpenChange={o => { if (!o) { setData(null); onClose(); } }}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Submissions — {hw?.title}</DialogTitle></DialogHeader>
                {!data ? (
                    <div className="text-sm text-brand-stone italic">Loading…</div>
                ) : (
                    <div className="space-y-3">
                        {data.submissions.length === 0 && data.missing.length === 0 && (
                            <div className="text-sm text-brand-stone italic text-center py-6">No students enrolled in this class yet.</div>
                        )}
                        {data.submissions.map(s => (
                            <SubmissionRow key={s.id} sub={s} onGraded={() => { load(); onGraded?.(); }} />
                        ))}
                        {data.missing.length > 0 && (
                            <div>
                                <div className="overline text-brand-stone mt-4 mb-2">Not submitted yet</div>
                                <div className="space-y-1.5">
                                    {data.missing.map(m => (
                                        <div key={m.student_id} className="text-sm border border-dashed border-border rounded-md px-3 py-2 flex items-center justify-between">
                                            <span>{m.student_name}</span>
                                            <Badge variant="secondary">Missing</Badge>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function SubmissionRow({ sub, onGraded }) {
    const [editing, setEditing] = React.useState(false);
    const [grade, setGrade] = React.useState(sub.grade || "");
    const [feedback, setFeedback] = React.useState(sub.feedback || "");
    const [busy, setBusy] = React.useState(false);
    async function save() {
        setBusy(true);
        try {
            await api.post(`/homework/submissions/${sub.id}/grade`, { grade, feedback });
            toast.success("Graded");
            setEditing(false);
            onGraded?.();
        } catch (err) { toast.error("Could not grade"); }
        finally { setBusy(false); }
    }
    return (
        <div className="border border-border rounded-lg p-3" data-testid={`sub-${sub.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium">{sub.student_name}</div>
                        {sub.status === "graded" ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border-0 gap-1"><CheckCircle2 className="w-3 h-3" />{sub.grade || "Graded"}</Badge>
                        ) : (
                            <Badge className="bg-amber-100 text-amber-800 border-0">Submitted</Badge>
                        )}
                        <span className="text-[11px] text-brand-stone">
                            {new Date(sub.submitted_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </span>
                    </div>
                    {sub.note && <p className="text-sm text-brand-stone mt-1 whitespace-pre-wrap">{sub.note}</p>}
                    <AttachmentList items={sub.attachments} testid={`sub-${sub.id}-att`} />
                    {sub.status === "graded" && sub.feedback && (
                        <div className="mt-2 border-l-2 border-primary/40 pl-3 text-sm italic text-brand-stone">
                            Feedback: {sub.feedback}
                        </div>
                    )}
                </div>
                <Button size="sm" variant="outline" onClick={() => setEditing(v => !v)} data-testid={`grade-toggle-${sub.id}`} className="gap-1.5 h-8">
                    <Pencil className="w-3.5 h-3.5" /> {sub.status === "graded" ? "Edit grade" : "Grade"}
                </Button>
            </div>
            {editing && (
                <div className="mt-3 space-y-2 border-t border-border pt-3">
                    <div className="grid grid-cols-3 gap-2">
                        <Input placeholder="Grade (A / 95 / ✓)" data-testid={`sub-${sub.id}-grade-input`} value={grade} onChange={e => setGrade(e.target.value)} />
                        <Textarea placeholder="Feedback…" data-testid={`sub-${sub.id}-feedback-input`} value={feedback} onChange={e => setFeedback(e.target.value)} rows={2} className="col-span-2" />
                    </div>
                    <Button size="sm" onClick={save} disabled={busy} data-testid={`save-grade-${sub.id}`} className="bg-primary hover:bg-primary/90">
                        {busy ? "Saving…" : "Save grade"}
                    </Button>
                </div>
            )}
        </div>
    );
}

/** Parent-side homework tab. */
export function ParentHomework() {
    const [items, setItems] = React.useState([]);
    const [busy, setBusy] = React.useState(null); // key hw.id+student.id being submitted

    const load = React.useCallback(() => {
        api.get("/homework/mine").then(r => setItems(r.data)).catch(() => setItems([]));
    }, []);
    React.useEffect(() => { load(); }, [load]);

    async function submit(hw, student, submission) {
        const key = `${hw.id}:${student.id}`;
        setBusy(key);
        try {
            await api.post(`/homework/${hw.id}/submit`, {
                student_id: student.id,
                note: submission.note,
                attachments: submission.attachments || [],
            });
            toast.success(`Submitted for ${student.first_name}`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Submit failed");
        } finally { setBusy(null); }
    }

    if (items.length === 0) {
        return <div className="text-brand-stone text-center py-16">No homework assigned yet.</div>;
    }
    return (
        <div className="space-y-3">
            {items.map(row => (
                <ParentHomeworkCard
                    key={row.id + ":" + row.student.id}
                    row={row}
                    onSubmit={(s) => submit(row, row.student, s)}
                    busy={busy === `${row.id}:${row.student.id}`}
                />
            ))}
        </div>
    );
}

function ParentHomeworkCard({ row, onSubmit, busy }) {
    const [note, setNote] = React.useState(row.submission?.note || "");
    const [attachments, setAttachments] = React.useState(row.submission?.attachments || []);
    const graded = row.submission?.status === "graded";
    const submitted = !!row.submission;
    return (
        <Card data-testid={`parent-hw-${row.id}-${row.student.id}`}>
            <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <BookOpen className="w-4 h-4 text-primary" />
                            <div className="font-dash text-lg font-semibold">{row.title}</div>
                            <Badge className="bg-primary/10 text-primary border-0">{row.student.first_name}</Badge>
                            <Badge variant="secondary">{row.class_name}</Badge>
                            {graded ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border-0 gap-1"><CheckCircle2 className="w-3 h-3" />{row.submission.grade || "Graded"}</Badge>
                            ) : submitted ? (
                                <Badge className="bg-amber-100 text-amber-800 border-0">Submitted</Badge>
                            ) : (
                                <Badge className="bg-rose-100 text-rose-800 border-0">To do</Badge>
                            )}
                        </div>
                        {row.description && <p className="text-sm text-brand-stone mt-1 whitespace-pre-wrap">{row.description}</p>}
                        {row.due_date && (
                            <div className="text-xs mt-1 text-brand-stone inline-flex items-center gap-1">
                                <Calendar className="w-3 h-3" /> Due {row.due_date}
                            </div>
                        )}
                        <AttachmentList items={row.attachments} testid={`parent-hw-${row.id}-teacher-att`} />
                    </div>
                </div>

                {graded && row.submission.feedback && (
                    <div className="mt-2 border-l-2 border-emerald-400 pl-3 text-sm italic">
                        Teacher feedback: {row.submission.feedback}
                    </div>
                )}

                {!graded && (
                    <div className="mt-4 border-t border-border pt-3 space-y-2">
                        <Label>Your submission</Label>
                        <Textarea
                            placeholder="Notes for teacher (optional)…"
                            data-testid={`parent-hw-note-${row.id}-${row.student.id}`}
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            rows={2}
                        />
                        <AttachmentPicker value={attachments} onChange={setAttachments} testid={`parent-hw-att-${row.id}-${row.student.id}`} />
                        <Button
                            onClick={() => onSubmit({ note, attachments })}
                            disabled={busy || (!note.trim() && attachments.length === 0)}
                            data-testid={`submit-hw-${row.id}-${row.student.id}`}
                            className="bg-primary hover:bg-primary/90"
                            size="sm"
                        >
                            {busy ? "Submitting…" : submitted ? "Re-submit" : "Submit homework"}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
